import isFinite from "lodash/isFinite";
import * as math from "mathjs";
import moment from "moment";

class UnitHelper {
  static i18nKeys = {
    Centimetre: "library.metric_notation_option_centimetres",
    Degree: "library.metric_notation_option_degree",
    FeetInches: "library.metric_notation_option_feet_inches",
    Inch: "library.metric_notation_option_inches",
    Kilocalorie: "library.metric_notation_option_calories",
    Kilogram: "library.metric_notation_option_kilograms",
    Kilojoule: "library.metric_notation_option_kilojoule",
    "Kilometres per Hour": "library.metric_notation_option_kilometres_per_hour",
    Metre: "library.metric_notation_option_metres",
    "Metres per Second": "library.metric_notation_option_metres_per_second",
    "Miles per Hour": "library.metric_notation_option_miles_per_hour",
    Newton: "library.metric_notation_option_newtons",
    "Newtons per Kilogram": "library.metric_notation_option_newtons_per_kilogram",
    Number: "library.metric_notation_option_number",
    Percentage: "library.metric_notation_option_percentage",
    Pound: "library.metric_notation_option_pounds",
    Second: "library.metric_notation_option_second",
    Time: "library.metric_notation_option_time",
    Watt: "library.metric_notation_option_watts",
    "Watts per Kilogram": "library.metric_notation_option_watts_per_kilogram",
    Yard: "library.metric_notation_option_yard",
  };

  static units = Object.freeze({
    Kilocalorie: "Kilocalorie",
    Centimetre: "Centimetre",
    Degree: "Degree",
    "Degrees per Second": "Degrees per Second",
    "Display Percentage": "Display Percentage",
    "Display Score": "Display Score",
    FeetInches: "FeetInches",
    Gravity: "Gravity",
    Inch: "Inch",
    Integer: "Integer",
    Joule: "Joule",
    Kilogram: "Kilogram",
    "Kilometres per Hour": "Kilometres per Hour",
    Kilojoule: "Kilojoule",
    Metre: "Metre",
    "Metres per Second Squared": "Metres per Second Squared",
    "Metres per Second": "Metres per Second",
    "Miles per Hour": "Miles per Hour",
    Newton: "Newton",
    "Newtons per Kilogram": "Newtons per Kilogram",
    Number: "Number",
    Percentage: "Percentage",
    Pound: "Pound",
    Second: "Second",
    Time: "Time",
    Watt: "Watt",
    "Watts per Kilogram": "Watts per Kilogram",
    Yard: "Yard",
  });

  static notations = {
    Centimetre: "cm",
    Degree: "°",
    "Degrees per Second": "°/s",
    "Display Percentage": "%",
    "Display Score": "",
    FeetInches: "", // special case for display
    Gravity: "G",
    Inch: '"',
    Integer: "", // No Unit
    Joule: "J",
    Kilocalorie: "kcal",
    Kilogram: "kg",
    "Kilometres per Hour": "km/h",
    Kilojoule: "kJ",
    Metre: "m",
    "Metres per Second": "m/s",
    "Miles per Hour": "mph",
    Newton: "N",
    "Newtons per Kilogram": "N/kg",
    Percentage: "%",
    Pound: "lb",
    Second: "s",
    // Time: "", // special case for display
    Watt: "W",
    "Watts per Kilogram": "W/kg",
    Yard: "yd",
  };

  static decimals = {
    Centimetre: 1,
    Degree: 0,
    "Degrees per Second": 1,
    "Display Percentage": 1,
    "Display Score": 0,
    FeetInches: 0,
    Gravity: 2,
    Inch: 1,
    Integer: 2, // No Unit
    Joule: 0,
    Kilocalorie: 0,
    Kilogram: 2,
    Kilojoule: 1,
    "Kilometres per Hour": 1,
    Metre: 2,
    "Metres per Second": 2,
    "Miles per Hour": 1,
    Newton: 0,
    "Newtons per Kilogram": 2,
    Percentage: 2,
    Pound: 1,
    Second: 3,
    Watt: 0,
    "Watts per Kilogram": 2,
    Yard: 2,
  };

  /**
   *
   * @param {Number|String} value - The value to format
   * @param {String|null} unit - The unit to format the value with (must be a key in UnitHelper.units, or null for no unit)
   * @param {Number|null|undefined} decimalOverride - The number of decimal places to format the value to (overrides the default decimal places for the unit)
   * @returns {String} The formatted value with unit notation
   */
  static format(value, unit, decimalOverride = undefined) {
    const decimal = decimalOverride ?? UnitHelper.decimals[unit] ?? UnitHelper.decimals.Integer;
    const notation = UnitHelper.notations[unit] || UnitHelper.notations.Integer;

    const formattedValue = parseFloat(value).toFixed(decimal);
    if (isNaN(formattedValue)) return value;

    // Only preserve trailing zeros for time-related units where precision matters
    const shouldPreserveTrailingZeros = unit === "Second";
    const displayValue = shouldPreserveTrailingZeros ? formattedValue : parseFloat(formattedValue);

    return [displayValue, notation].join("");
  }

  /**
   * @description Format a value with a unit.
   * If decimals is provided, it will override the default decimal places for the unit
   * Otherwise, it will use the default decimal places for the unit or the default decimal places for Integer (2 decimal places)
   *
   * @param {Number} value
   * @param {String|null} unit
   * @param {Number|null|undefined} decimals
   * @returns {Number} The formatted value
   */
  static formatDecimals(value, unit, decimals) {
    const decimal = decimals ?? UnitHelper.decimals[unit] ?? UnitHelper.decimals.Integer;
    // Return the formatted string to preserve trailing zeros, but parse to number if needed for calculations
    const formatted = value.toFixed(decimal);
    return parseFloat(formatted);
  }

  static _converters = {
    Metre: {
      Centimetre: (value) =>
        math.unit(value, UnitHelper.notations.Metre).to(UnitHelper.notations.Centimetre).toNumber(),
      Inch: (metre) => math.unit(metre, UnitHelper.notations.Metre).to("inch").toNumber(),
      FeetInches: (metre) => {
        const base = math.unit(metre, UnitHelper.notations.Metre).to("inch").toNumber();
        const [feet, inches] = math.unit(Math.round(base), UnitHelper.units.Inch.toLowerCase()).splitUnit(["ft", "in"]);

        return [feet.toNumber().toFixed(0), inches.toNumber().toFixed(0), base];
      },
      Yard: (metre) => math.unit(metre, UnitHelper.notations.Metre).to(UnitHelper.notations.Yard).toNumber(),
    },
    FeetInches: {
      Metre: (value) => {
        if (value === null || value === undefined) {
          return null;
        }

        // If it's an array [feet, inches], use it directly
        if (Array.isArray(value) && value.length >= 2) {
          const [feet, inches] = value;
          const mathFt = math.unit(feet, "ft");
          const mathInch = math.unit(Number(inches), "in");
          const base = math.add(mathFt, mathInch).to("m").toNumber();
          return base;
        }

        // If it's a single number, treat it as total inches (common for user input)
        if (typeof value === "number" && !isNaN(value)) {
          const mathInch = math.unit(value, "in");
          return mathInch.to("m").toNumber();
        }

        return null;
      },
    },
    Centimetre: {
      Metre: (value) => math.unit(value, UnitHelper.notations.Centimetre).to(UnitHelper.notations.Metre).toNumber(),
      FeetInches: (centimetre) => {
        const base = math.unit(centimetre, UnitHelper.notations.Centimetre).to("inch").toNumber();
        const [feet, inches] = math.unit(math.round(base), UnitHelper.units.Inch.toLowerCase()).splitUnit(["ft", "in"]);

        return [feet.toNumber().toFixed(0), inches.toNumber().toFixed(0), base];
      },
    },
    Kilogram: {
      Pound: (kg) => math.unit(kg, UnitHelper.notations.Kilogram).to(UnitHelper.notations.Pound).toNumber(),
    },
    Pound: {
      Kilogram: (pound) => math.unit(pound, UnitHelper.notations.Pound).to(UnitHelper.notations.Kilogram).toNumber(),
    },
    Joule: {
      Kilojoule: (joule) => math.unit(joule, UnitHelper.notations.Joule).to(UnitHelper.notations.Kilojoule).toNumber(),
      Kilocalorie: (joule) => (isFinite(joule) ? joule / 4184 : null),
    },
    Inch: {
      Centimetre: (inches) => math.unit(inches, "inch").to(UnitHelper.notations.Centimetre).toNumber(),
      Metre: (inches) => math.unit(inches, "inch").to(UnitHelper.notations.Metre).toNumber(),
    },
    Percentage: {
      "Display Percentage": (percentage) => percentage * 100,
    },
    "Metres per Second Squared": {
      Gravity: (mpss) => math.unit(mpss, "m/s^2").toNumber() / 9.80665,
    },
    "Metres per Second": {
      "Kilometres per Hour": (mps) => math.unit(mps, "m/s").to(UnitHelper.notations["Kilometres per Hour"]).toNumber(),
      "Miles per Hour": (mps) => math.unit(mps, "m/s").to("mi/h").toNumber(),
    },
    "Kilometres per Hour": {
      "Metres per Second": (mps) => math.unit(mps, UnitHelper.notations["Kilometres per Hour"]).to("m/s").toNumber(),
    },
    "Miles per Hour": {
      "Metres per Second": (val) => math.unit(val, "mi/h").to("m/s").toNumber(),
    },
    Yard: {
      Metre: (yard) => math.unit(yard, UnitHelper.notations.Yard).to(UnitHelper.notations.Metre).toNumber(),
    },
    "Display Percentage": {
      Percentage: (displayPercentage) => displayPercentage / 100,
    },
    score: {
      "Display Score": (score) => score * 100,
    },
    "Display Score": {
      score: (displayScore) => displayScore / 100,
    },
    Kilocalorie: {
      Joule: (kcal) => (isFinite(kcal) ? kcal * 4184 : null),
    },
  };

  static convert(value, from, to) {
    const fromUnit = UnitHelper._converters[from];
    const converter = fromUnit && fromUnit[to];

    /**
     * FeetInches is an array of two numbers,
     * so we need to check if any of the numbers are NaN
     */
    const isNaNcheck = Array.isArray(value) ? value.some((v) => isNaN(v)) : isNaN(value);

    if (value === null || isNaNcheck || !converter) {
      return value;
    }

    return converter(value);
  }

  static formatTime(seconds) {
    if (isFinite(seconds) === false) {
      return seconds;
    }

    const duration = moment.duration(seconds, "seconds");

    /**
     * @type {string[]} */
    const resultArray = [];

    if (duration.hours() > 0) {
      resultArray.push(`${duration.hours()}h`);
    }
    if (duration.minutes() > 0) {
      resultArray.push(`${duration.minutes()}m`);
    }
    if (duration.seconds() > 0 || duration.milliseconds() > 0) {
      const totalSecondsDecimal = seconds - duration.hours() * 3600 - duration.minutes() * 60;
      const secondsValue = totalSecondsDecimal.toFixed(3).replace(/\.?0+$/, "");
      resultArray.push(`${secondsValue}s`);
    }

    return resultArray.join(" ");
  }

  static formatTimeForCharts(seconds) {
    if (isFinite(seconds) === false) {
      return seconds;
    }

    const duration = moment.duration(seconds, "seconds");

    let isSecondsIncluded = seconds < 3600;
    let isMillisecondsIncluded = isSecondsIncluded && seconds < 60;

    /**
     * @type {string[]} */
    const resultArray = [];

    if (duration.hours() > 0) {
      resultArray.push(`${duration.hours()}h`);
    }
    if (duration.minutes() > 0) {
      resultArray.push(`${duration.minutes()}m`);
    }
    if (isSecondsIncluded || (isMillisecondsIncluded && duration.milliseconds() > 0)) {
      const totalSecondsDecimal = seconds - duration.hours() * 3600 - duration.minutes() * 60;
      const secondsValue = isMillisecondsIncluded
        ? totalSecondsDecimal.toFixed(3).replace(/\.?0+$/, "")
        : totalSecondsDecimal.toFixed(0);
      resultArray.push(`${secondsValue}s`);
    }

    return resultArray.join(" ");
  }

  static process(value, { from, to, decimals }) {
    const number = UnitHelper.convert(value, from, to);

    const formatAs = to || from;

    if (to === "FeetInches") {
      const [feet, inches] = number;

      return `${feet}'${inches}"`;
    }

    if (to === "Time") {
      return UnitHelper.formatTime(value);
    }

    return UnitHelper.format(number, formatAs, decimals);
  }

  /**
   * @description Format a value with a unit
   *
   * Part of the new implementation that returns an array of results, and a formatted string.
   * Results can be used to display value and unit separately in the UI. For example, to display a value in a larger font size.
   *
   * @param {number|null|undefined} [decimalOverride=undefined] - The number of decimal places to format the value to (overrides the default decimal places for the unit)
   */
  static formatUnit(value, unit, decimalOverride = undefined) {
    const decimal = decimalOverride ?? UnitHelper.decimals[unit] ?? UnitHelper.decimals.Integer;

    const notation = UnitHelper.getUnitNotation(unit);

    const formattedValue = parseFloat(value).toFixed(decimal);
    if (isNaN(formattedValue)) {
      return {
        results: [
          {
            value: value,
            unit: notation,
            formatted: value,
          },
        ],
        value: value,
        formatted: value,
      };
    }

    // Only preserve trailing zeros for time-related units where precision matters
    const shouldPreserveTrailingZeros = unit === "Second";
    const displayValue = shouldPreserveTrailingZeros ? formattedValue : parseFloat(formattedValue);
    const formatted = `${displayValue}${notation}`;
    const parsed = parseFloat(formattedValue); // Only for numeric value

    return {
      results: [
        {
          value: parsed,
          unit: notation,
          formatted: formatted,
        },
      ],
      value: parsed,
      formatted: formatted,
    };
  }

  static appendUnit(value, unit) {
    const notation = UnitHelper.getUnitNotation(unit);

    // if feet and inches, return as a string
    if (unit === "FeetInches") {
      const [feet, inches] = value;
      return `${feet}'${inches}"`;
    }

    return `${value}${notation}`;
  }

  /**
   *
   * @param {string | null} unit
   * @returns {string} The unit notation
   */
  static getUnitNotation(unit) {
    if (unit === "Inch") return "in";
    return UnitHelper.notations[unit] || UnitHelper.notations.Integer;
  }

  /**
   * @description Convert a value from one unit to another
   *
   * Part of the new implementation that returns an array of results, and a formatted string.
   * Results can be used to display value and unit separately in the UI. For example, to display a value in a larger font size.
   */
  static processUnit(value, { from, to, decimals }) {
    const number = UnitHelper.convert(value, from, to);

    const formatAs = to || from;

    if (formatAs === "FeetInches") {
      const [feet, inches, base] = number;

      return {
        results: [
          {
            value: feet,
            unit: "'",
            formatted: `${feet}'`,
          },
          {
            value: inches,
            unit: '"',
            formatted: `${inches}"`,
          },
        ],
        value: base,
        formatted: `${feet}'${inches}"`,
      };
    }
    if (formatAs === "Time") {
      const totalSeconds =
        typeof decimals === "number" && isFinite(value) ? parseFloat(value.toFixed(decimals)) : value;
      const duration = moment.duration(totalSeconds, "seconds");

      const milliseconds = duration.milliseconds();
      // The milliseconds are the fraction of the seconds, so 3 ms is .003 and not .3.
      const seconds = parseFloat((duration.seconds() + milliseconds / 1000).toFixed(3));

      return {
        results: [
          {
            value: duration.hours(),
            unit: "h",
            formatted: `${duration.hours()}s`,
          },
          {
            value: duration.minutes(),
            unit: "m",
            formatted: `${duration.minutes()}s`,
          },
          {
            value: seconds,
            unit: "s",
            formatted: `${seconds}s`,
          },
        ].filter(({ value }) => Boolean(value)),
        value: totalSeconds,
        formatted: UnitHelper.formatTime(totalSeconds),
      };
    }

    return UnitHelper.formatUnit(number, formatAs, decimals);
  }
}
export default UnitHelper;
